import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const heroImg =
  "https://images.unsplash.com/photo-1760172287483-02d382f63a6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjB0ZXh0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzcyMTIzNTB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    const dest = user.role === "voucher_staff" ? "/voucher" : "/";
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-900/60" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Om Jewels"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
        <div className="relative text-white max-w-md">
          <div className="overline text-gold-300 mb-3">For Wholesalers</div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
            Custom orders.<br /> Real karigars.<br /> One panel.
          </h1>
          <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
            Track every image-based order from the first WhatsApp message to the final invoice — without the spreadsheets.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="overline text-zinc-500 mb-2">Sign in</div>
            <h2 className="font-heading font-black text-3xl tracking-tighter">Welcome back</h2>
            <p className="text-sm text-zinc-500 mt-1">Use your admin or staff credentials.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <div className="text-right">
            <a href="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-800 hover:underline">
              Forgot password?
            </a>
          </div>

          {error ? (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
              data-testid="login-error"
            >
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            className="w-full bg-zinc-900 text-white hover:bg-zinc-800 h-11"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Sign in
          </Button>

          <div className="text-xs text-zinc-500 text-center pt-4 border-t border-border">
            Contact your administrator for credentials
          </div>
        </form>
      </div>
    </div>
  );
}
